/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api, { viewsAPI } from '../lib/api';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Send, Star, Loader2, CheckCircle2, BarChart3,
  GripVertical, ChevronUp, ChevronDown, Clock, X,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (url) => url?.startsWith('/') ? `${API_URL}${url}` : url;

const ImageLightbox = ({ src, alt, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid="image-lightbox">
    <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-testid="lightbox-close">
      <X className="w-6 h-6" />
    </button>
    <img src={src} alt={alt || ''} className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg shadow-2xl" onClick={e => e.stopPropagation()} />
  </div>
);

const ClickableImage = ({ src, alt, className }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <img src={src} alt={alt || ''} className={`${className} cursor-zoom-in`} onClick={e => { e.preventDefault(); e.stopPropagation(); setOpen(true); }} data-testid="clickable-image" />
      {open && <ImageLightbox src={src} alt={alt} onClose={() => setOpen(false)} />}
    </>
  );
};

const RatingInput = ({ value, onChange, max = 5 }) => (
  <div className="flex items-center gap-1">
    {Array.from({ length: max }, (_, i) => i + 1).map(n => (
      <button key={n} type="button" onClick={() => onChange(n)} className="focus:outline-none">
        <Star className={`w-8 h-8 transition-colors ${n <= (value || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-200 hover:text-amber-200'}`} />
      </button>
    ))}
    {value > 0 && <span className="ml-2 text-sm text-slate-500">{value}/{max}</span>}
  </div>
);

const RankingInput = ({ options, value, onChange }) => {
  const items = value && value.length ? value : options.map(o => o.text);
  const move = (idx, dir) => {
    const arr = [...items];
    const tgt = idx + dir;
    if (tgt < 0 || tgt >= arr.length) return;
    [arr[idx], arr[tgt]] = [arr[tgt], arr[idx]];
    onChange(arr);
  };
  return (
    <div className="space-y-2">
      {items.map((item, idx) => (
        <div key={`option-${idx}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <span className="text-sm font-bold text-slate-400 w-6">{idx + 1}.</span>
          <span className="flex-1 text-sm">{item}</span>
          <div className="flex flex-col gap-0.5">
            <button type="button" onClick={() => move(idx, -1)} disabled={idx === 0} className="p-0.5 disabled:opacity-30"><ChevronUp className="w-4 h-4 text-slate-400" /></button>
            <button type="button" onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="p-0.5 disabled:opacity-30"><ChevronDown className="w-4 h-4 text-slate-400" /></button>
          </div>
        </div>
      ))}
    </div>
  );
};

export default function SurveyTakePage() {
  const { surveyId } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  usePageTitle('Anket');
  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [basicResults, setBasicResults] = useState(null);

  const fetchSurvey = async () => {
    try {
      const res = await api.get(`/surveys/${surveyId}`);
      setSurvey(res.data);
      if (res.data.my_response) {
        setAnswers(res.data.my_response.answers || {});
        setSubmitted(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Anket yuklenemedi');
      navigate('/panel/anketler');
    } finally {
      setLoading(false);
    }
  };

  const fetchBasicResults = async () => {
    try {
      const res = await api.get(`/surveys/${surveyId}/public-results`);
      setBasicResults(res.data);
    } catch {}
  };

  useEffect(() => {
    fetchSurvey();
  }, [surveyId]);

  useEffect(() => {
    if (surveyId) viewsAPI.track('survey', surveyId).catch(() => {});
  }, [surveyId]);

  useEffect(() => {
    if (submitted && hasPermission('surveys.view_results')) fetchBasicResults();
  }, [submitted]);

  const setAnswer = (qId, value) => setAnswers(prev => ({ ...prev, [qId]: value }));

  const toggleCheckbox = (qId, val) => {
    setAnswers(prev => {
      const arr = prev[qId] || [];
      return { ...prev, [qId]: arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val] };
    });
  };

  const shouldShow = (question, idx) => {
    const cond = question.settings?.conditional_logic;
    if (!cond) return true;
    const depIdx = cond.question_index;
    if (depIdx === undefined || depIdx === null) return true;
    const depQ = survey.questions[depIdx];
    if (!depQ) return true;
    const depAnswer = answers[depQ.id];
    return String(depAnswer) === String(cond.value);
  };

  const handleSubmit = async () => {
    // Validate required
    for (const q of (survey.questions || [])) {
      if (q.is_required && shouldShow(q, 0)) {
        const a = answers[q.id];
        if (!a || (Array.isArray(a) && a.length === 0)) {
          toast.error(`"${q.question_text}" sorusu zorunludur`);
          return;
        }
      }
    }
    setSubmitting(true);
    try {
      await api.post(`/surveys/${surveyId}/respond`, { answers });
      toast.success('Cevabiniz kaydedildi!');
      setSubmitted(true);
      // Refetch survey to get updated state
      const res = await api.get(`/surveys/${surveyId}`);
      setSurvey(res.data);
      // Fetch public results (sadece view_results yetkisi olanlar icin)
      if (hasPermission('surveys.view_results')) {
        try {
          const resResults = await api.get(`/surveys/${surveyId}/public-results`);
          setBasicResults(resResults.data);
        } catch {}
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cevap gonderilemedi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-600" /></div>;
  if (!survey) return null;

  const isExpired = survey.end_date && new Date(survey.end_date) < new Date();
  const canRespond = survey.status === 'aktif' && !isExpired && (!survey.has_responded || survey.allow_update);
  const canViewResults = hasPermission('surveys.view_results');

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="survey-take-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/panel/anketler')} data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-slate-900">{survey.title}</h1>
          {survey.description && <p className="text-sm text-slate-500 mt-1">{survey.description}</p>}
        </div>
        {canViewResults && (
          <Link to={`/panel/anketler/${surveyId}/sonuclar`}>
            <Button variant="outline" size="sm" data-testid="view-results-btn">
              <BarChart3 className="w-4 h-4 mr-1" /> Sonuclar
            </Button>
          </Link>
        )}
      </div>

      {/* Info bar */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {survey.end_date && (
          <Badge variant="outline" className="text-xs"><Clock className="w-3 h-3 mr-1" /> Bitis: {survey.end_date}</Badge>
        )}
        <Badge variant="outline" className="text-xs">{survey.response_count || 0} katilim</Badge>
        {survey.has_responded && !survey.allow_update && (
          <Badge className="bg-emerald-100 text-emerald-700 text-xs"><CheckCircle2 className="w-3 h-3 mr-1" /> Zaten katildiniz</Badge>
        )}
        {survey.has_responded && survey.allow_update && (
          <Badge className="bg-amber-100 text-amber-700 text-xs">Cevabinizi guncelleyebilirsiniz</Badge>
        )}
      </div>

      {/* Questions */}
      {(survey.questions || []).map((q, idx) => {
        if (!shouldShow(q, idx)) return null;
        return (
          <div key={q.id} className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid={`take-q-${idx}`}>
            <div className="flex items-start gap-3">
              <span className="text-lg font-bold text-blue-600">{idx + 1}</span>
              <div className="flex-1">
                <p className="font-medium text-slate-900">
                  {q.question_text}
                  {q.is_required && <span className="text-red-500 ml-1">*</span>}
                </p>
                {q.description && <p className="text-sm text-slate-400 mt-1">{q.description}</p>}
              </div>
            </div>

            {/* Radio */}
            {q.question_type === 'radio' && (
              <div className="space-y-2 pl-8">
                {(q.options || []).map((opt, oi) => (
                  <label key={oi} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    answers[q.id] === opt.text ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                  }`}>
                    <input type="radio" name={q.id} checked={answers[q.id] === opt.text}
                      onChange={() => setAnswer(q.id, opt.text)} className="accent-blue-600" disabled={!canRespond} />
                    {opt.image && <ClickableImage src={imgSrc(opt.image)} alt={opt.text} className="h-12 w-12 rounded-md object-cover flex-shrink-0" />}
                    <span className="text-sm">{opt.text}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Checkbox */}
            {q.question_type === 'checkbox' && (
              <div className="space-y-2 pl-8">
                {(q.options || []).map((opt, oi) => (
                  <label key={oi} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                    (answers[q.id] || []).includes(opt.text) ? 'border-blue-300 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                  }`}>
                    <Checkbox checked={(answers[q.id] || []).includes(opt.text)}
                      onCheckedChange={() => canRespond && toggleCheckbox(q.id, opt.text)} disabled={!canRespond} />
                    {opt.image && <ClickableImage src={imgSrc(opt.image)} alt={opt.text} className="h-12 w-12 rounded-md object-cover flex-shrink-0" />}
                    <span className="text-sm">{opt.text}</span>
                  </label>
                ))}
              </div>
            )}

            {/* Text */}
            {q.question_type === 'text' && (
              <div className="pl-8">
                <Input value={answers[q.id] || ''} onChange={e => setAnswer(q.id, e.target.value)}
                  placeholder="Cevabinizi yazin..." disabled={!canRespond} />
              </div>
            )}

            {/* Textarea */}
            {q.question_type === 'textarea' && (
              <div className="pl-8">
                <textarea className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                  rows={4} value={answers[q.id] || ''} onChange={e => setAnswer(q.id, e.target.value)}
                  placeholder="Cevabinizi yazin..." disabled={!canRespond} />
              </div>
            )}

            {/* Rating / Emoji */}
            {(q.question_type === 'rating' || q.question_type === 'emoji') && (
              <div className="pl-8">
                <RatingInput value={answers[q.id] || 0} onChange={v => canRespond && setAnswer(q.id, v)} />
              </div>
            )}

            {/* Yes/No */}
            {q.question_type === 'yes_no' && (
              <div className="flex gap-3 pl-8">
                {['Evet', 'Hayir'].map(val => (
                  <button key={val} type="button" disabled={!canRespond}
                    onClick={() => setAnswer(q.id, val)}
                    className={`px-6 py-2.5 rounded-lg border-2 text-sm font-medium transition-all ${
                      answers[q.id] === val
                        ? val === 'Evet' ? 'border-green-500 bg-green-50 text-green-700' : 'border-red-500 bg-red-50 text-red-700'
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                    }`}>
                    {val}
                  </button>
                ))}
              </div>
            )}

            {/* Image Choice */}
            {q.question_type === 'image_choice' && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pl-8">
                {(q.options || []).map((opt, oi) => (
                  <button key={oi} type="button" disabled={!canRespond}
                    onClick={() => setAnswer(q.id, opt.text)}
                    className={`p-3 rounded-lg border-2 text-center transition-all ${
                      answers[q.id] === opt.text ? 'border-blue-500 bg-blue-50' : 'border-slate-200 hover:border-slate-300'
                    }`}>
                    {opt.image && <ClickableImage src={imgSrc(opt.image)} alt={opt.text} className="w-full h-24 object-cover rounded mb-2" />}
                    <span className="text-sm font-medium">{opt.text}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Ranking */}
            {q.question_type === 'ranking' && (
              <div className="pl-8">
                <RankingInput options={q.options || []} value={answers[q.id] || []}
                  onChange={v => canRespond && setAnswer(q.id, v)} />
              </div>
            )}
          </div>
        );
      })}

      {/* Submit */}
      {canRespond && !submitted && (
        <div className="flex justify-end gap-3 pb-8">
          <Button size="lg" onClick={handleSubmit} disabled={submitting} data-testid="submit-survey-btn">
            {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Send className="w-5 h-5 mr-2" />}
            {survey.has_responded ? 'Cevabi Guncelle' : 'Gonder'}
          </Button>
        </div>
      )}

      {/* Results Summary - shown after submission */}
      {submitted && basicResults && (
        <div className="space-y-4 pb-8" data-testid="survey-public-results">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
            <div className="flex items-center gap-3 mb-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              <h2 className="text-lg font-semibold text-emerald-800">Katiliminiz icin tesekkurler!</h2>
            </div>
            <p className="text-sm text-emerald-700">Toplam <strong>{basicResults.total_responses}</strong> kisi bu ankete katildi.</p>
            {survey.allow_update && (
              <Button variant="outline" size="sm" className="mt-3" onClick={() => setSubmitted(false)} data-testid="update-response-btn">
                Cevabimi Guncelle
              </Button>
            )}
          </div>

          <h3 className="text-base font-semibold text-slate-800">Sonuclar</h3>

          {basicResults.question_results.map((qr, idx) => (
            <div key={qr.question_id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`public-result-${idx}`}>
              <p className="font-medium text-slate-800 mb-3">
                <span className="text-blue-600 mr-2">{idx + 1}.</span>
                {qr.question_text}
              </p>

              {/* Distribution bars */}
              {qr.distribution && Object.keys(qr.distribution).length > 0 && (
                <div className="space-y-2">
                  {Object.entries(qr.distribution)
                    .sort((a, b) => b[1] - a[1])
                    .map(([label, count]) => {
                      const pct = qr.total_answers > 0 ? (count / qr.total_answers) * 100 : 0;
                      return (
                        <div key={label}>
                          <div className="flex items-center justify-between text-sm mb-1">
                            <span className="text-slate-700">{label}</span>
                            <span className="text-slate-500 font-medium">{count} ({pct.toFixed(0)}%)</span>
                          </div>
                          <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className="h-full bg-blue-500 rounded-full transition-all duration-500" style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}

              {/* Average for rating */}
              {qr.average !== undefined && (
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1,2,3,4,5].map(n => (
                      <Star key={n} className={`w-5 h-5 ${n <= Math.round(qr.average) ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />
                    ))}
                  </div>
                  <span className="text-sm font-semibold text-amber-700">{qr.average} / 5</span>
                  <span className="text-xs text-slate-400">({qr.total_answers} oy)</span>
                </div>
              )}

              {/* Text - just show count */}
              {qr.question_type === 'text' || qr.question_type === 'textarea' ? (
                <p className="text-sm text-slate-500">{qr.total_answers} kisi cevapladi</p>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
